'use client';

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download, TrendingUp } from 'lucide-react';

type Sprint = {
  id: string;
  name: string;
  committed: number;
  completed: number;
};

const STORAGE_KEY = 'planetroadmap.sprint-velocity.v1';

function newSprint(n: number): Sprint {
  return {
    id: Math.random().toString(36).slice(2),
    name: `Sprint ${n}`,
    committed: 30,
    completed: 25,
  };
}

export function SprintVelocityCalculator() {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Sprint[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSprints(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setSprints([
      { ...newSprint(1), committed: 32, completed: 28 },
      { ...newSprint(2), committed: 28, completed: 30 },
      { ...newSprint(3), committed: 35, completed: 31 },
      { ...newSprint(4), committed: 30, completed: 27 },
      { ...newSprint(5), committed: 32, completed: 33 },
    ]);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sprints));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [sprints, hydrated]);

  const stats = useMemo(() => {
    if (sprints.length === 0) return { avg: 0, min: 0, max: 0, completionRate: 0, suggestion: 0 };
    const completed = sprints.map((s) => s.completed);
    const avg = completed.reduce((a, b) => a + b, 0) / completed.length;
    const min = Math.min(...completed);
    const max = Math.max(...completed);
    const totalCommitted = sprints.reduce((a, b) => a + b.committed, 0);
    const totalCompleted = sprints.reduce((a, b) => a + b.completed, 0);
    const completionRate = totalCommitted ? (totalCompleted / totalCommitted) * 100 : 0;
    // Suggested capacity: 80% of average velocity (rounded), to leave buffer for unplanned work
    const suggestion = Math.round(avg * 0.8);
    return { avg, min, max, completionRate, suggestion };
  }, [sprints]);

  function update(id: string, patch: Partial<Sprint>) {
    setSprints((curr) => curr.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function remove(id: string) {
    setSprints((curr) => curr.filter((s) => s.id !== id));
  }

  function add() {
    setSprints((curr) => [...curr, newSprint(curr.length + 1)]);
  }

  function reset() {
    if (!confirm('Clear all sprints?')) return;
    setSprints([newSprint(1)]);
  }

  function exportCsv() {
    const rows = [
      ['Sprint', 'Committed', 'Completed'],
      ...sprints.map((s) => [s.name, s.committed, s.completed]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sprint-velocity.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Average velocity" value={stats.avg.toFixed(1)} accent />
        <StatCard label="Min / max" value={`${stats.min} – ${stats.max}`} />
        <StatCard label="Completion rate" value={`${stats.completionRate.toFixed(0)}%`} />
        <StatCard label="Suggested next" value={stats.suggestion.toFixed(0)} accent />
      </div>

      {/* Suggested capacity callout */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex gap-3 items-start">
        <TrendingUp className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Plan your next sprint at ~{stats.suggestion} points.</span>{' '}
          <span className="text-muted-foreground">
            That is 80% of your average completed velocity, leaving buffer for unplanned work, support, and
            review.
          </span>
        </div>
      </div>

      {/* Sprint table */}
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Sprint</th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Committed</th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Completed</th>
                <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Hit rate</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sprints.map((s, i) => {
                const rate = s.committed ? (s.completed / s.committed) * 100 : 0;
                return (
                  <tr
                    key={s.id}
                    className={i < sprints.length - 1 ? 'border-b border-border/40' : ''}
                  >
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        value={s.name}
                        onChange={(e) => update(s.id, { name: e.target.value })}
                        className="w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="number"
                        min={0}
                        value={s.committed}
                        onChange={(e) =>
                          update(s.id, { committed: Math.max(0, Number(e.target.value)) })
                        }
                        className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="number"
                        min={0}
                        value={s.completed}
                        onChange={(e) =>
                          update(s.id, { completed: Math.max(0, Number(e.target.value)) })
                        }
                        className="w-20 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1"
                      />
                    </td>
                    <td className="py-2 px-4 text-right font-mono text-xs text-muted-foreground">
                      {rate.toFixed(0)}%
                    </td>
                    <td className="py-2 px-2">
                      <button
                        onClick={() => remove(s.id)}
                        aria-label="Remove sprint"
                        className="text-muted-foreground hover:text-red-500 p-1 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {sprints.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-muted-foreground">
                    No sprints yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all"
        >
          <Plus className="w-4 h-4" />
          Add sprint
        </button>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={sprints.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-2xl p-4 ${
        accent ? 'bg-primary/5 border border-primary/20' : 'bg-white dark:bg-card border border-border/60'
      }`}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? 'text-primary' : ''}`}>{value}</div>
    </div>
  );
}
